import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion.tsx'
import { type ReactElement, type ReactNode, isValidElement, useMemo, useState } from 'react'

import {
  SortableAndSelectableStoryListElementWithFormToggler,
  StoryListElementWithFormToggler,
} from '@/components/StoryListElement.tsx'
import { StoryFormWithAttachment } from '@/components/form/StoryForm.tsx'
import { NEW_STORY_ID } from '@/constants/DEFAULT_VALUES.ts'
import { STORY_STATUSES } from '@/constants/STORY_STATUSES.ts'
import { STORY_TYPES } from '@/constants/STORY_TYPES.ts'
import { getNow } from '@/utils/date.ts'
import { isAccepted, isInProgress } from '@/utils/status.ts'

type Props = {
  uniqueKey: string
  stories: Story[]
  isDraggableStories?: boolean
  children: ReactNode
  footer?: ReactNode
  isClosedByDefault?: boolean
}
export const Divider = ({ uniqueKey, stories, isDraggableStories, children, footer, isClosedByDefault }: Props) => {
  const [openItems, setOpenItems] = useState<string[]>(isClosedByDefault ? [] : [uniqueKey])

  const isOpen = openItems.includes(uniqueKey)

  const toggleAccordion = () => {
    setOpenItems((prev) => (prev.includes(uniqueKey) ? prev.filter((key) => key !== uniqueKey) : [...prev, uniqueKey]))
  }

  return (
    <li className="rounded-sm overflow-hidden">
      <Accordion type="multiple" value={openItems} onValueChange={setOpenItems}>
        <AccordionItem value={uniqueKey}>
          {isValidElement(children) && typeof children.type === 'function' ? (
            <children.type
              {...((children as ReactElement).props as object)}
              isOpen={isOpen}
              toggleAccordion={toggleAccordion}
            />
          ) : (
            <AccordionTrigger className="p-0 flex-row-reverse">{children}</AccordionTrigger>
          )}
          <AccordionContent className="pb-0">
            <ul className="flex flex-col gap-[1px] ">
              {stories.map((story) => (
                <ListElement key={story.id} story={story} isDraggableEnabled={isDraggableStories} />
              ))}
              {footer}
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </li>
  )
}

type ListElementProps = {
  story: Story
  isDraggableEnabled?: boolean
}

const ListElement = ({ story, isDraggableEnabled }: ListElementProps) => {
  const sideBorderStyle = useMemo(() => {
    switch (true) {
      case story.id === NEW_STORY_ID:
        return 'border-surface-storyStatusBar-toDo'
      case story.storyType === STORY_TYPES.RELEASE &&
        story.status !== STORY_STATUSES.ACCEPTED &&
        story.status !== STORY_STATUSES.UNSCHEDULED &&
        story.deadline !== null &&
        getNow() > story.deadline:
        return 'border-surface-releaseMarker-overdue'
      case story.storyType === STORY_TYPES.RELEASE:
        return 'border-surface-storyStatusBar-release'
      case isAccepted(story.status):
        return 'border-surface-storyStatusBar-done'
      case isInProgress(story.status):
        return 'border-surface-storyStatusBar-WIP'
      default:
        return 'border-surface-storyStatusBar-toDo'
    }
  }, [story])

  const component = useMemo(() => {
    switch (true) {
      case story.id === NEW_STORY_ID:
        return <StoryFormWithAttachment storyId={story.id} />
      case isDraggableEnabled:
        return <SortableAndSelectableStoryListElementWithFormToggler story={story} />
      default:
        return <StoryListElementWithFormToggler story={story} />
    }
  }, [story, isDraggableEnabled])

  return (
    <li key={story.id} className={`border-l-4 bg-surface-backgroundElevated ${sideBorderStyle}`}>
      {component}
    </li>
  )
}
